// ==================== VERIFY EMAIL PAGE ====================

import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mail, 
  CheckCircle2, 
  AlertCircle, 
  ArrowRight,
  RefreshCw,
  ShieldCheck
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { ROUTES } from '../../constants';
import { authService } from '../../services';
import { fadeInUp, staggerContainer } from '../../utils/animations';

// ==================== CONSTANTS ====================

const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 60; // seconds

// ==================== SUCCESS VIEW ====================

const SuccessView = () => {
  const navigate = useNavigate();

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      className="text-center py-8"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
        className="w-20 h-20 mx-auto mb-6 rounded-full bg-(--color-success)/20 flex items-center justify-center"
      >
        <CheckCircle2 size={40} className="text-(--color-success)" />
      </motion.div>
      
      <h2 className="text-2xl font-heading font-bold text-white mb-3">
        Email Verified Successfully!
      </h2>
      
      <p className="text-(--color-soft-gray) mb-6 max-w-sm mx-auto">
        Your email has been verified. You can now access all features of AmplifyMsg.
      </p>

      <Button 
        variant="primary" 
        rightIcon={<ArrowRight size={18} />}
        onClick={() => navigate(ROUTES.AUTH.LOGIN)}
      >
        Continue to Login
      </Button>
    </motion.div>
  );
};

// ==================== MAIN COMPONENT ====================

const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || '';
  
  const [otp, setOtp] = useState<string[]>(new Array(OTP_LENGTH).fill(''));
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Redirect if no email in state
  useEffect(() => {
    if (!email) {
      navigate(ROUTES.AUTH.REGISTER);
    }
  }, [email, navigate]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Auto-focus first input
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = useCallback((index: number, value: string) => {
    // Only allow numbers
    if (value && !/^\d+$/.test(value)) return;

    const newOtp = [...otp];
    
    // Handle multi-digit input (from paste via onChange)
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, OTP_LENGTH);
      for (let i = 0; i < OTP_LENGTH; i++) {
        newOtp[i] = digits[i] || '';
      }
      setOtp(newOtp);
      // Focus last input if complete
      if (digits.length >= OTP_LENGTH) {
        inputRefs.current[OTP_LENGTH - 1]?.focus();
      }
      return;
    }

    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  }, [otp]);

  // Handle paste event directly for better UX
  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    // Extract only digits from pasted content
    const digits = pastedData.replace(/\D/g, '').slice(0, OTP_LENGTH);
    
    if (digits.length > 0) {
      const newOtp = new Array(OTP_LENGTH).fill('');
      for (let i = 0; i < digits.length && i < OTP_LENGTH; i++) {
        newOtp[i] = digits[i];
      }
      setOtp(newOtp);
      
      // Focus the last filled input or last input if complete
      const focusIndex = Math.min(digits.length, OTP_LENGTH) - 1;
      inputRefs.current[focusIndex]?.focus();
    }
  }, []);

  const handleKeyDown = useCallback((index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (!otp[index] && index > 0) {
        // Move to previous input if current is empty
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  }, [otp]);

  const resetOtpInputs = useCallback(() => {
    setOtp(new Array(OTP_LENGTH).fill(''));
    // Focus first input after a short delay to ensure state is updated
    setTimeout(() => {
      inputRefs.current[0]?.focus();
    }, 100);
  }, []);

  const handleVerify = async () => {
    const otpCode = otp.join('');
    
    if (otpCode.length !== OTP_LENGTH) {
      setError('Please enter the complete verification code');
      return;
    }

    setError(null);
    setIsVerifying(true);

    try {
      const response = await authService.verifyEmail(email, otpCode);
      
      if (response.success) {
        setIsSuccess(true);
      } else {
        setError(response.message || 'Verification failed. Please try again.');
        resetOtpInputs();
      }
    } catch {
      setError('An error occurred. Please try again.');
      resetOtpInputs();
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;

    setError(null);
    setIsResending(true);

    try {
      const response = await authService.resendVerificationOtp(email);
      
      if (response.success) {
        setResendCooldown(RESEND_COOLDOWN);
        setOtp(new Array(OTP_LENGTH).fill(''));
        inputRefs.current[0]?.focus();
      } else {
        setError(response.message || 'Failed to resend code. Please try again.');
      }
    } catch {
      setError('An error occurred. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  // Auto-verify when all digits are entered
  useEffect(() => {
    const otpCode = otp.join('');
    if (otpCode.length === OTP_LENGTH && !isVerifying) {
      handleVerify();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  // Show success view
  if (isSuccess) {
    return <SuccessView />;
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="animate-fade-in"
    >
      {/* Header */}
      <motion.div variants={fadeInUp} className="text-center mb-8">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-(--color-signal-orange)/20 flex items-center justify-center">
          <ShieldCheck size={32} className="text-(--color-signal-orange)" />
        </div>
        <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white mb-2">
          Verify Your Email
        </h1>
        <p className="text-(--color-muted-gray)">
          We've sent a verification code to
        </p>
        <p className="text-(--color-signal-orange) font-medium mt-1">
          {email}
        </p>
      </motion.div>

      {/* Error Message */}
      <AnimatePresence mode="wait">
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -10, height: 0 }}
            transition={{ duration: 0.3 }}
            className="flex items-start gap-3 p-4 rounded-md bg-red-500/10 border border-red-500/30 mb-6"
            role="alert"
          >
            <AlertCircle size={20} className="text-red-500 shrink-0 mt-0.5" />
            <p className="text-sm text-red-400">{error}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* OTP Input */}
      <motion.div variants={fadeInUp} className="mb-6">
        <div className="flex justify-center gap-3">
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => { inputRefs.current[index] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={OTP_LENGTH}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              autoComplete="one-time-code"
              className="w-12 h-14 text-center text-xl font-bold text-white bg-(--color-navy-light) 
                         border-2 border-(--color-border-gray) rounded-lg 
                         focus:border-(--color-signal-orange) focus:outline-none focus:ring-2 
                         focus:ring-(--color-signal-orange)/20 transition-all"
              disabled={isVerifying}
            />
          ))}
        </div>
      </motion.div>

      {/* Verify Button */}
      <motion.div variants={fadeInUp}>
        <Button
          variant="primary"
          size="lg"
          fullWidth
          isLoading={isVerifying}
          onClick={handleVerify}
          disabled={otp.join('').length !== OTP_LENGTH}
        >
          Verify Email
        </Button>
      </motion.div>

      {/* Resend Code */}
      <motion.div variants={fadeInUp} className="mt-6 text-center">
        <p className="text-sm text-(--color-muted-gray) mb-2">
          Didn't receive the code?
        </p>
        <button
          onClick={handleResend}
          disabled={resendCooldown > 0 || isResending}
          className="inline-flex items-center gap-2 text-sm text-(--color-signal-orange) 
                     hover:text-(--color-orange-light) font-medium transition-colors
                     disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isResending ? (
            <>
              <RefreshCw size={16} className="animate-spin" />
              Sending...
            </>
          ) : resendCooldown > 0 ? (
            <>Resend code in {resendCooldown}s</>
          ) : (
            <>
              <RefreshCw size={16} />
              Resend Code
            </>
          )}
        </button>
      </motion.div>

      {/* Info Box */}
      <motion.div 
        variants={fadeInUp}
        className="mt-8 p-4 bg-(--color-navy-light)/50 rounded-lg border border-(--color-border-gray)"
      >
        <div className="flex items-start gap-3">
          <Mail size={20} className="text-(--color-signal-orange) shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-white mb-1">Check your inbox</p>
            <p className="text-xs text-(--color-muted-gray)">
              The verification code was sent to your email. If you don't see it, 
              check your spam folder or click "Resend Code" above.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Back to Register */}
      <motion.div variants={fadeInUp} className="mt-6 text-center">
        <Link
          to={ROUTES.AUTH.REGISTER}
          className="text-sm text-(--color-muted-gray) hover:text-white transition-colors"
        >
          ← Back to Registration
        </Link>
      </motion.div>
    </motion.div>
  );
};

export { VerifyEmailPage };

